import type { User } from '@einhaus/project314-types';
import { type ReactNode, createContext, useContext, useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { trpc, trpcClient, trpcUnbatched } from '@/api/trpc';
import { useQueryClient } from '@tanstack/react-query';
import { useAppQuery } from '@/hooks/useAppQuery';
import { handleTrpcQuery } from '@/api/trpcResponseHandler';
import { useTrpcMutation } from '@/api/useTrpcMutation';
import { clearPendingTwoFactorSetupEmail, hasPendingTwoFactorSetup, setPendingTwoFactorSetupEmail } from '@/utils/twoFactorOnboarding';
import type { PendingRequiredAgreement } from '@/utils/agreements';
import type { SupportWriteScope } from '@/utils/supportWriteScopes';
import { clearAuthScopedQueries } from './authQueryCache';
import { browserSupportsWebAuthn, startAuthentication, startRegistration } from '@simplewebauthn/browser';
import { showToast } from '@/components/Layout/Toast';

type BrowserAuthenticationResponse = Awaited<ReturnType<typeof startAuthentication>>;
type BrowserRegistrationResponse = Awaited<ReturnType<typeof startRegistration>>;
type SupportStartSessionInput = Parameters<typeof trpcClient.support.startSession.mutate>[0];
type SupportStepUpPayload = SupportStartSessionInput['stepUp'];
type SupportPasskeyAuthenticationPayload = Extract<SupportStepUpPayload, { method: 'passkey' }>['authenticationResponse'];
type SupportPasskeyRegistrationPayload = Parameters<typeof trpcClient.support.finishPasskeyRegistration.mutate>[0]['registrationResponse'];

const normalizeClientExtensionResults = (
    clientExtensionResults: BrowserAuthenticationResponse['clientExtensionResults']
): Record<string, unknown> => Object.fromEntries(Object.entries(clientExtensionResults));

const normalizeSupportAuthenticationResponse = (
    authenticationResponse: BrowserAuthenticationResponse
): SupportPasskeyAuthenticationPayload => ({
    id: authenticationResponse.id,
    rawId: authenticationResponse.rawId,
    response: {
        clientDataJSON: authenticationResponse.response.clientDataJSON,
        authenticatorData: authenticationResponse.response.authenticatorData,
        signature: authenticationResponse.response.signature,
        ...(authenticationResponse.response.userHandle !== undefined ? { userHandle: authenticationResponse.response.userHandle } : {})
    },
    ...(authenticationResponse.authenticatorAttachment !== undefined
        ? { authenticatorAttachment: authenticationResponse.authenticatorAttachment }
        : {}),
    clientExtensionResults: normalizeClientExtensionResults(authenticationResponse.clientExtensionResults),
    type: authenticationResponse.type
});

const normalizeSupportRegistrationResponse = (registrationResponse: BrowserRegistrationResponse): SupportPasskeyRegistrationPayload => ({
    id: registrationResponse.id,
    rawId: registrationResponse.rawId,
    response: {
        clientDataJSON: registrationResponse.response.clientDataJSON,
        attestationObject: registrationResponse.response.attestationObject,
        ...(registrationResponse.response.authenticatorData !== undefined
            ? { authenticatorData: registrationResponse.response.authenticatorData }
            : {}),
        ...(registrationResponse.response.transports !== undefined ? { transports: registrationResponse.response.transports } : {}),
        ...(registrationResponse.response.publicKeyAlgorithm !== undefined
            ? { publicKeyAlgorithm: registrationResponse.response.publicKeyAlgorithm }
            : {}),
        ...(registrationResponse.response.publicKey !== undefined ? { publicKey: registrationResponse.response.publicKey } : {})
    },
    ...(registrationResponse.authenticatorAttachment !== undefined
        ? { authenticatorAttachment: registrationResponse.authenticatorAttachment }
        : {}),
    clientExtensionResults: normalizeClientExtensionResults(registrationResponse.clientExtensionResults),
    type: registrationResponse.type
});

const PASSKEY_APPROVAL_INCOMPLETE_MESSAGE =
    'Passkey approval was not completed. If this browser says there are no passkeys on this device, use "Register Another Passkey" first or switch to a browser or device that already has one.';

type AuthenticatedUser = Pick<User, 'id' | 'email' | '2faEnabled' | 'type' | 'settings'>;

interface SupportSessionSummary {
    id: number;
    mode: 'readOnly' | 'limitedWrite';
    reason: string;
    ticketReference: string | null;
    stepUpMethod: 'totp' | 'passkey';
    allowedWriteScopes: SupportWriteScope[];
    expiresDateTime: string;
    absoluteExpiresDateTime: string;
}

interface SupportStepUpState {
    hasPasskey: boolean;
    passkeyCount: number;
    requiredMethod: 'totp' | 'passkey';
}

interface SessionPolicy {
    supportSessionEnabled: boolean;
    canAccessAdminRoutes: boolean;
    canUseAgentChat: boolean;
    canManage2fa: boolean;
    canManageBrokerConnections: boolean;
    canManageMcpAccessTokens: boolean;
    canManageSharedResources: boolean;
    canManageUserSettings: boolean;
    canClosePositions: boolean;
    canWriteUserData: boolean;
}

const DEFAULT_SESSION_POLICY: SessionPolicy = {
    supportSessionEnabled: false,
    canAccessAdminRoutes: false,
    canUseAgentChat: true,
    canManage2fa: true,
    canManageBrokerConnections: true,
    canManageMcpAccessTokens: true,
    canManageSharedResources: true,
    canManageUserSettings: true,
    canClosePositions: true,
    canWriteUserData: true
};

interface AuthContextType {
    user: AuthenticatedUser | null;
    actorUser: AuthenticatedUser | null;
    isLoading: boolean;
    isAuthenticated: boolean;
    isImpersonating: boolean;
    supportSession: SupportSessionSummary | null;
    supportStepUp: SupportStepUpState | null;
    sessionPolicy: SessionPolicy;
    requiresTwoFactorSetup: boolean;
    pendingRequiredAgreements: PendingRequiredAgreement[];
    hasPendingRequiredAgreements: boolean;
    login: (username: string, password: string, twoFactorCode?: string) => Promise<void>;
    register: (params: {
        email: string;
        password: string;
        password2: string;
        inviteCode: string;
        betaAgreementVersion: string;
    }) => Promise<void>;
    logout: () => Promise<void>;
    startSupportSession: (params: {
        targetUserId: number;
        reason: string;
        requestedMode?: 'readOnly' | 'limitedWrite';
        requestedWriteScopes?: SupportWriteScope[];
        ticketReference?: string | null;
        totpCode?: string;
    }) => Promise<void>;
    stopSupportSession: () => Promise<void>;
    extendSupportSession: (params?: { totpCode?: string }) => Promise<void>;
    registerSupportPasskey: () => Promise<void>;
    refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | null>(null);

export const useAuth = () => {
    const context = useContext(AuthContext);

    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }

    return context;
};

interface AuthProviderProps {
    children: ReactNode;
}

export const AuthProvider = ({ children }: AuthProviderProps) => {
    const [isLoading, setIsLoading] = useState(false);
    const queryClient = useQueryClient();
    const userDetailsQueryOptions = useMemo(() => trpcUnbatched.user.fetchDetails.queryOptions(), []);
    const userDetailsQueryKey = userDetailsQueryOptions.queryKey;
    const previousResolvedSessionKeyRef = useRef<string | undefined>(undefined);

    const clearAuthScopedQueryCache = useCallback(async () => {
        await clearAuthScopedQueries(queryClient, [userDetailsQueryKey]);
    }, [queryClient, userDetailsQueryKey]);

    // Fetch user details with tRPC
    const userDetailsQuery = useAppQuery({
        ...userDetailsQueryOptions,
        refetchOnWindowFocus: false
    });

    const user = useMemo<AuthenticatedUser | null>(() => {
        if (!userDetailsQuery.data?.success || !userDetailsQuery.data.user) return null;

        return userDetailsQuery.data.user;
    }, [userDetailsQuery.data]);

    const actorUser = useMemo<AuthenticatedUser | null>(() => {
        if (!userDetailsQuery.data?.success) return null;
        return userDetailsQuery.data.actorUser ?? null;
    }, [userDetailsQuery.data]);

    const isImpersonating = useMemo(
        () => Boolean(userDetailsQuery.data?.success && userDetailsQuery.data.isImpersonating),
        [userDetailsQuery.data]
    );

    const supportSession = useMemo<SupportSessionSummary | null>(() => {
        if (!userDetailsQuery.data?.success) return null;
        const session = userDetailsQuery.data.supportSession ?? null;

        if (!session) {
            return null;
        }

        return {
            ...session,
            allowedWriteScopes: session.allowedWriteScopes ?? []
        };
    }, [userDetailsQuery.data]);

    const supportStepUp = useMemo<SupportStepUpState | null>(() => {
        if (!userDetailsQuery.data?.success) {
            return null;
        }

        return userDetailsQuery.data.supportStepUp ?? null;
    }, [userDetailsQuery.data]);

    const sessionPolicy = useMemo<SessionPolicy>(() => {
        if (!userDetailsQuery.data?.success) {
            return DEFAULT_SESSION_POLICY;
        }

        return userDetailsQuery.data.sessionPolicy ?? DEFAULT_SESSION_POLICY;
    }, [userDetailsQuery.data]);

    const requiresTwoFactorSetup = useMemo(() => {
        if (!user || isImpersonating) return false;

        const userHasTwoFactorEnabled = Boolean(user['2faEnabled']);

        return hasPendingTwoFactorSetup(user.email, userHasTwoFactorEnabled);
    }, [isImpersonating, user]);

    const pendingRequiredAgreements = useMemo<PendingRequiredAgreement[]>(
        () => (userDetailsQuery.data?.success ? (userDetailsQuery.data.pendingRequiredAgreements ?? []) : []),
        [userDetailsQuery.data]
    );

    // Handle user details side effects
    useEffect(() => {
        if (userDetailsQuery.isLoading) {
            return;
        }

        if (userDetailsQuery.error) {
            handleTrpcQuery(userDetailsQuery, {
                showErrorToast: false,
                setError: (msg) => {
                    console.error(`Error fetching user details: ${msg}`);
                }
            });

            return;
        }

        if (userDetailsQuery.data?.success && !isImpersonating && userDetailsQuery.data.user?.['2faEnabled']) {
            clearPendingTwoFactorSetupEmail();
        }
    }, [isImpersonating, userDetailsQuery.error, userDetailsQuery.data]);

    useEffect(() => {
        if (userDetailsQuery.isLoading || userDetailsQuery.isError) {
            return;
        }

        const currentSessionKey = `${user?.id ?? 'null'}:${isImpersonating ? (supportSession?.id ?? 'impersonating') : 'direct'}`;
        const previousSessionKey = previousResolvedSessionKeyRef.current;

        if (previousSessionKey !== undefined && previousSessionKey !== currentSessionKey) {
            clearAuthScopedQueryCache().catch((error) => {
                console.error('Failed to clear auth-scoped query cache:', error);
            });
        }

        previousResolvedSessionKeyRef.current = currentSessionKey;
    }, [clearAuthScopedQueryCache, isImpersonating, supportSession?.id, user?.id, userDetailsQuery.isError, userDetailsQuery.isLoading]);

    const refreshUser = useCallback(async () => {
        await queryClient.invalidateQueries({
            queryKey: userDetailsQueryKey
        });

        await queryClient.refetchQueries({
            queryKey: userDetailsQueryKey,
            type: 'active'
        });
    }, [queryClient, userDetailsQueryKey]);

    const buildSupportStepUpPayload = useCallback(
        async (totpCode?: string): Promise<SupportStepUpPayload> => {
            if (supportStepUp?.requiredMethod === 'passkey') {
                const isSupported = browserSupportsWebAuthn();

                if (!isSupported) {
                    throw new Error('This browser does not support passkeys required for support approval.');
                }

                const passkeyChallenge = await trpcClient.support.beginPasskeyAuthentication.mutate();
                let authenticationResponse: BrowserAuthenticationResponse;

                try {
                    authenticationResponse = await startAuthentication({
                        optionsJSON: passkeyChallenge.optionsJSON
                    });
                } catch {
                    throw new Error(PASSKEY_APPROVAL_INCOMPLETE_MESSAGE);
                }

                return {
                    method: 'passkey' as const,
                    challengeId: passkeyChallenge.challengeId,
                    authenticationResponse: normalizeSupportAuthenticationResponse(authenticationResponse)
                };
            }

            if (!totpCode || !/^\d{6}$/.test(totpCode)) {
                throw new Error('Enter a valid 6-digit TOTP code.');
            }

            return {
                method: 'totp' as const,
                totpCode
            };
        },
        [supportStepUp]
    );

    // Setup login mutation with useTrpcMutation helper
    const loginMutation = useTrpcMutation(
        trpc.auth.login.mutationOptions({
            onSuccess: async () => {
                await clearAuthScopedQueryCache();
                queryClient.setQueryData(userDetailsQueryKey, undefined);

                await queryClient.invalidateQueries({
                    queryKey: userDetailsQueryKey
                });
            }
        }),
        {
            // No success message here - we'll handle it in the login function
            fallbackErrorMessage: 'Login failed'
        }
    );

    // Setup register mutation with useTrpcMutation helper
    const registerMutation = useTrpcMutation(
        trpc.auth.register.mutationOptions({
            onSuccess: () => {
                // We'll handle login after registration in the register function
            }
        }),
        {
            fallbackErrorMessage: 'Registration failed'
        }
    );

    // Setup logout mutation with useTrpcMutation helper
    const logoutMutation = useTrpcMutation(
        trpc.auth.logout.mutationOptions({
            onSuccess: async () => {
                await clearAuthScopedQueryCache();

                queryClient.setQueryData(userDetailsQueryKey, undefined);

                await queryClient.invalidateQueries({
                    queryKey: userDetailsQueryKey
                });
            }
        }),
        {
            successMessage: 'You have been logged out.',
            fallbackErrorMessage: 'Failed to logout user'
        }
    );

    const startSupportSessionMutation = useTrpcMutation(
        trpc.support.startSession.mutationOptions({
            onSuccess: async () => {
                await clearAuthScopedQueryCache();
                queryClient.setQueryData(userDetailsQueryKey, undefined);

                await queryClient.invalidateQueries({
                    queryKey: userDetailsQueryKey
                });
            }
        }),
        {
            successMessage: 'Support session started.',
            fallbackErrorMessage: 'Failed to start support session'
        }
    );

    const stopSupportSessionMutation = useTrpcMutation(
        trpc.support.stopSession.mutationOptions({
            onSuccess: async () => {
                await clearAuthScopedQueryCache();
                queryClient.setQueryData(userDetailsQueryKey, undefined);

                await queryClient.invalidateQueries({
                    queryKey: userDetailsQueryKey
                });
            }
        }),
        {
            successMessage: 'Support session ended.',
            fallbackErrorMessage: 'Failed to stop support session'
        }
    );

    const extendSupportSessionMutation = useTrpcMutation(
        trpc.support.extendSession.mutationOptions({
            onSuccess: async () => {
                await queryClient.invalidateQueries({
                    queryKey: userDetailsQueryKey
                });
            }
        }),
        {
            successMessage: 'Support session extended.',
            fallbackErrorMessage: 'Failed to extend support session'
        }
    );

    // Custom login mutation to handle 2FA properly
    const login = useCallback(
        async (username: string, password: string, twoFactorCode?: string) => {
            try {
                setIsLoading(true);

                // We need to handle the 2FA case specially to avoid duplicate toasts
                // Call the mutation directly
                const response = await loginMutation.mutateAsync({
                    email: username,
                    password,
                    twoFactorCode
                });

                if (!response.success) {
                    // Check for 2FA requirement
                    if (response.errorMessages?.some((msg) => msg.includes('2FA code is required.'))) {
                        throw new Error('2FA_REQUIRED');
                    }

                    // Let other errors be handled by the mutation
                    throw new Error(response.errorMessages?.[0] || 'Login failed');
                }
            } finally {
                setIsLoading(false);
            }
        },
        [loginMutation]
    );

    // Register function
    const register = useCallback(
        async (params: { email: string; password: string; password2: string; inviteCode: string; betaAgreementVersion: string }) => {
            try {
                setIsLoading(true);

                const registerResponse = await registerMutation.mutateAsync({
                    betaAgreementVersion: params.betaAgreementVersion,
                    email: params.email,
                    inviteCode: params.inviteCode,
                    password: params.password,
                    password2: params.password2
                });

                if (!registerResponse.success) {
                    throw new Error(registerResponse.errorMessages?.[0] || 'Registration failed');
                }

                setPendingTwoFactorSetupEmail(params.email);

                // If registration is successful, immediately log the user in
                await login(params.email, params.password);
            } finally {
                setIsLoading(false);
            }
        },
        [login, registerMutation]
    );

    // Logout function
    const logout = useCallback(async () => {
        try {
            setIsLoading(true);
            await logoutMutation.mutateAsync();
        } finally {
            setIsLoading(false);
        }
    }, [logoutMutation]);

    const startSupportSession = useCallback(
        async (params: {
            targetUserId: number;
            reason: string;
            requestedMode?: 'readOnly' | 'limitedWrite';
            requestedWriteScopes?: SupportWriteScope[];
            ticketReference?: string | null;
            totpCode?: string;
        }) => {
            try {
                setIsLoading(true);
                let stepUp;

                try {
                    stepUp = await buildSupportStepUpPayload(params.totpCode);
                } catch (error) {
                    const errorMessage = error instanceof Error ? error.message : 'Failed to start support session';
                    showToast(errorMessage, 'error');
                    throw error;
                }

                await startSupportSessionMutation.mutateAsync({
                    targetUserId: params.targetUserId,
                    reason: params.reason,
                    ticketReference: params.ticketReference ?? null,
                    stepUp,
                    requestedMode: params.requestedMode ?? 'readOnly',
                    requestedWriteScopes: params.requestedWriteScopes ?? []
                });
            } finally {
                setIsLoading(false);
            }
        },
        [buildSupportStepUpPayload, startSupportSessionMutation]
    );

    const stopSupportSession = useCallback(async () => {
        try {
            setIsLoading(true);
            await stopSupportSessionMutation.mutateAsync();
        } finally {
            setIsLoading(false);
        }
    }, [stopSupportSessionMutation]);

    const extendSupportSession = useCallback(
        async (params?: { totpCode?: string }) => {
            try {
                setIsLoading(true);
                let stepUp;

                try {
                    stepUp = await buildSupportStepUpPayload(params?.totpCode);
                } catch (error) {
                    const errorMessage = error instanceof Error ? error.message : 'Failed to extend support session';
                    showToast(errorMessage, 'error');
                    throw error;
                }

                await extendSupportSessionMutation.mutateAsync({
                    stepUp
                });
            } finally {
                setIsLoading(false);
            }
        },
        [buildSupportStepUpPayload, extendSupportSessionMutation]
    );

    const registerSupportPasskey = useCallback(async () => {
        try {
            setIsLoading(true);

            const isSupported = browserSupportsWebAuthn();

            if (!isSupported) {
                throw new Error('This browser does not support passkey registration.');
            }

            const registrationChallenge = await trpcClient.support.beginPasskeyRegistration.mutate();

            const registrationResponse = await startRegistration({
                optionsJSON: registrationChallenge.optionsJSON
            });

            const registrationResult = await trpcClient.support.finishPasskeyRegistration.mutate({
                challengeId: registrationChallenge.challengeId,
                registrationResponse: normalizeSupportRegistrationResponse(registrationResponse)
            });

            if (!registrationResult.success) {
                throw new Error(registrationResult.errorMessages?.[0] || 'Failed to register support passkey');
            }

            await refreshUser();
            showToast('Support passkey registered.', 'success');
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Failed to register support passkey';
            showToast(errorMessage, 'error');
            throw error;
        } finally {
            setIsLoading(false);
        }
    }, [refreshUser]);

    return (
        <AuthContext.Provider
            value={{
                user,
                actorUser,
                isLoading:
                    isLoading ||
                    userDetailsQuery.isLoading ||
                    loginMutation.isPending ||
                    registerMutation.isPending ||
                    logoutMutation.isPending ||
                    startSupportSessionMutation.isPending ||
                    stopSupportSessionMutation.isPending ||
                    extendSupportSessionMutation.isPending,
                isAuthenticated: !!user,
                isImpersonating,
                supportSession,
                supportStepUp,
                sessionPolicy,
                requiresTwoFactorSetup,
                pendingRequiredAgreements,
                hasPendingRequiredAgreements: pendingRequiredAgreements.length > 0,
                login,
                register,
                logout,
                startSupportSession,
                stopSupportSession,
                extendSupportSession,
                registerSupportPasskey,
                refreshUser
            }}
        >
            {children}
        </AuthContext.Provider>
    );
};
